import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, MapPin, Clock, ArrowRight, Users, Globe, 
  TrendingUp, Building2, Send, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = '';

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    cover_letter: ''
  });

  const jobs = [
    {
      id: 'intl-property-advisor',
      title: 'International Property Advisor',
      department: 'Sales',
      location: 'Pune, India',
      type: 'Full-time',
      experience: '3-5 years',
      description: 'Join our elite team of property advisors helping Indian HNIs discover global investment opportunities.',
      responsibilities: [
        'Guide HNI clients through international property investment process',
        'Build and maintain relationships with high-value clients',
        'Stay updated on global real estate markets and visa programs',
        'Coordinate with international developers and legal teams',
        'Achieve monthly and quarterly sales targets',
        'Conduct property presentations and virtual tours'
      ],
      requirements: [
        '3-5 years experience in luxury real estate or wealth management',
        'Excellent communication and presentation skills',
        'Strong network among HNI/UHNI communities',
        'Willingness to travel internationally',
        'Fluency in English and Hindi; other languages a plus',
        'MBA or equivalent qualification preferred'
      ],
      benefits: [
        'Competitive base salary + uncapped commission',
        'International travel opportunities',
        'Health insurance for family',
        'Professional development programs',
        'Flexible work arrangements'
      ]
    },
    {
      id: 'brand-strategist',
      title: 'Brand Strategist',
      department: 'Marketing',
      location: 'Pune, India',
      type: 'Full-time',
      experience: '4-6 years',
      description: 'Shape the Mulberry World brand and position us as the premier gateway for Indian investors to global real estate.',
      responsibilities: [
        'Develop and execute brand strategy for luxury positioning',
        'Create compelling narratives for different markets and audiences',
        'Manage relationships with PR agencies and media partners',
        'Oversee content strategy across all channels',
        'Collaborate with design team on visual identity',
        'Analyze brand metrics and optimize campaigns'
      ],
      requirements: [
        '4-6 years experience in brand strategy or luxury marketing',
        'Portfolio showcasing luxury/premium brand work',
        'Strong understanding of HNI consumer behavior',
        'Excellent writing and storytelling skills',
        'Experience with digital marketing and analytics',
        'MBA in Marketing or relevant qualification'
      ],
      benefits: [
        'Competitive salary package',
        'Creative freedom and autonomy',
        'Work with global teams and partners',
        'Learning and development budget',
        'Hybrid work model'
      ]
    },
    {
      id: 'sales-associate',
      title: 'Sales Associate',
      department: 'Sales',
      location: 'Pune, India',
      type: 'Full-time',
      experience: '1-3 years',
      description: 'Start your career in international real estate and grow with a dynamic team serving elite clientele.',
      responsibilities: [
        'Support senior advisors in client management',
        'Handle inbound inquiries and qualify leads',
        'Prepare property presentations and documentation',
        'Coordinate client meetings and property viewings',
        'Maintain CRM and track sales pipeline',
        'Assist with market research and competitor analysis'
      ],
      requirements: [
        '1-3 years experience in sales or customer service',
        'Bachelor\'s degree in any discipline',
        'Excellent verbal and written communication',
        'Proficiency in MS Office and CRM tools',
        'Self-motivated with strong work ethic',
        'Interest in real estate and investment markets'
      ],
      benefits: [
        'Attractive base + performance bonus',
        'Fast-track career growth opportunities',
        'Training and mentorship programs',
        'Health insurance',
        'Young and dynamic work culture'
      ]
    }
  ];

  const handleApply = async (e) => {
    e.preventDefault();
    setIsApplying(true);

    try {
      await axios.post(`${API_BASE_URL}/api/careers/apply`, {
        ...applicationData,
        job_id: selectedJob.id,
        job_title: selectedJob.title
      });
      
      toast.success('Application submitted successfully! We\'ll get back to you soon.');
      setApplicationData({
        name: '',
        email: '',
        phone: '',
        experience: '',
        cover_letter: ''
      });
      setSelectedJob(null);
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="careers-page">
      {/* Hero */}
      <section className="py-24 border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="label-overline">Join Our Team</span>
            <h1 className="text-4xl sm:text-5xl text-white mt-4 mb-4">
              Build the Future of <br />
              <span className="text-gradient-gold">Global Real Estate</span>
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Join Mulberry World and be part of a team that's transforming how Indian 
              investors access global property markets. We're looking for passionate 
              individuals who share our vision of excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-[#1A1D24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl text-white text-center mb-12">Why Join Mulberry World?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Globe, title: 'Global Exposure', desc: 'Work with international developers and clients across continents' },
              { icon: TrendingUp, title: 'Growth Opportunities', desc: 'Fast-track your career in the luxury real estate sector' },
              { icon: Users, title: 'Elite Network', desc: 'Build connections with HNIs and industry leaders' },
              { icon: Building2, title: 'Premium Brand', desc: 'Represent a brand trusted by discerning investors' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg text-white font-medium mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl text-white mb-8">Open Positions</h2>
          
          <div className="space-y-6">
            {jobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-6 hover:border-[#D4AF37]/50 transition-all"
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">
                        {job.department}
                      </Badge>
                      <Badge className="bg-[#1A1D24] text-[#A1A1AA] border border-[#2A2F3A]">
                        {job.type}
                      </Badge>
                    </div>
                    <h3 className="text-xl text-white font-medium mb-2">{job.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#A1A1AA]">
                      <span className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {job.location}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {job.experience}
                      </span>
                    </div>
                  </div>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        className="btn-luxury"
                        onClick={() => setSelectedJob(job)}
                        data-testid={`apply-btn-${job.id}`}
                      >
                        View & Apply <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#1A1D24] border-[#2A2F3A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl">{job.title}</DialogTitle>
                      </DialogHeader>
                      
                      <div className="mt-4 space-y-6">
                        <p className="text-[#A1A1AA]">{job.description}</p>
                        
                        <div>
                          <h4 className="text-white font-medium mb-3">Responsibilities</h4>
                          <ul className="space-y-2">
                            {job.responsibilities.map((item, i) => (
                              <li key={i} className="flex items-start text-sm text-[#A1A1AA]">
                                <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-white font-medium mb-3">Requirements</h4>
                          <ul className="space-y-2">
                            {job.requirements.map((item, i) => (
                              <li key={i} className="flex items-start text-sm text-[#A1A1AA]">
                                <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-white font-medium mb-3">Benefits</h4>
                          <ul className="space-y-2">
                            {job.benefits.map((item, i) => (
                              <li key={i} className="flex items-start text-sm text-[#A1A1AA]">
                                <CheckCircle className="h-4 w-4 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Application Form */}
                        <div className="border-t border-[#2A2F3A] pt-6">
                          <h4 className="text-white font-medium mb-4">Apply Now</h4>
                          <form onSubmit={handleApply} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-white mb-2 block">Full Name *</Label>
                                <Input
                                  value={applicationData.name}
                                  onChange={(e) => setApplicationData({...applicationData, name: e.target.value})}
                                  className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                                  required
                                />
                              </div>
                              <div>
                                <Label className="text-white mb-2 block">Email *</Label>
                                <Input
                                  type="email"
                                  value={applicationData.email}
                                  onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                                  className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                                  required
                                />
                              </div>
                              <div>
                                <Label className="text-white mb-2 block">Phone *</Label>
                                <Input
                                  value={applicationData.phone}
                                  onChange={(e) => setApplicationData({...applicationData, phone: e.target.value})}
                                  className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                                  required
                                />
                              </div>
                              <div>
                                <Label className="text-white mb-2 block">Years of Experience *</Label>
                                <Input
                                  value={applicationData.experience}
                                  onChange={(e) => setApplicationData({...applicationData, experience: e.target.value})}
                                  className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                                  required
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-white mb-2 block">Cover Letter *</Label>
                              <Textarea
                                value={applicationData.cover_letter}
                                onChange={(e) => setApplicationData({...applicationData, cover_letter: e.target.value})}
                                placeholder="Tell us why you're a great fit for this role..."
                                className="bg-[#0B0C10] border-[#2A2F3A] text-white min-h-[100px]"
                                required
                              />
                            </div>
                            <Button 
                              type="submit" 
                              className="w-full btn-luxury"
                              disabled={isApplying}
                            >
                              {isApplying ? 'Submitting...' : 'Submit Application'}
                              <Send className="ml-2 h-4 w-4" />
                            </Button>
                          </form>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <p className="text-[#A1A1AA] text-sm mt-4">{job.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1D24]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl text-white mb-4">Don't See a Perfect Fit?</h2>
          <p className="text-[#A1A1AA] mb-6">
            We're always looking for exceptional talent. Send us your resume and we'll 
            keep you in mind for future opportunities.
          </p>
          <a href="mailto:info@mulberryleaf.in?subject=General Application - Mulberry World">
            <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0C10]">
              Send Your Resume
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
